import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import NextImage from 'next/image'
import NextLink from 'next/link'
import CodeBlock from 'components/CodeBlock'
import TextWithImages from 'components/TextWithImages'
import { ReactNode } from 'react'
import {
  Asset,
  BlogPostBody,
  CaseStudyBody,
  CaseStudyGroupBody,
  AlternatingLayoutItemText,
  TextWithImagesText,
  AlternatingLayout as AlternatingLayoutEntry,
  CodeBlock as CodeBlockEntry,
  BlogPost as BlogPostEntry,
  CaseStudy as CaseStudyEntry,
  HighlightedLink as HighlightedLinkEntry,
  TextWithImages as TextWithImagesEntry,
  GraphiQl as GraphiQlEntry,
  ComponentVideo as ComponentVideoEntry,
  CaseStudyPublicBlurb,
} from 'types/generated/contentful'
import AlternatingLayout from 'components/AlternatingLayout'
import HighlightedLink from 'components/HighlightedLink'
import { CASE_STUDY_SLUG } from '../../common/constants'
import { Video } from '../Video'
import dynamic from 'next/dynamic'

const GraphiQL = dynamic(() => import('components/GraphiQL'), {
  ssr: false,
})

interface RichTextRendererProps extends React.HTMLAttributes<HTMLDivElement> {
  data: CaseStudyBody | BlogPostBody | CaseStudyGroupBody | CaseStudyPublicBlurb
}

export default function RichTextRenderer({
  data,
  className,
  ...props
}: RichTextRendererProps): JSX.Element {
  return (
    <div className={`rich-text ${className || ''}`} {...props}>
      {renderRichText(data)}
    </div>
  )
}

// Copied from https://www.contentful.com/blog/2021/04/14/rendering-linked-assets-entries-in-contentful/
export function renderRichText(
  data:
    | CaseStudyBody
    | BlogPostBody
    | CaseStudyGroupBody
    | AlternatingLayoutItemText
    | TextWithImagesText
    | CaseStudyPublicBlurb,
): ReactNode {
  const { links, json } = data

  const assetMap = new Map<string, Asset>()
  for (const asset of links.assets.block || []) {
    if (!asset) return
    assetMap.set(asset.sys.id, asset)
  }

  type ExpectedEntryTypes =
    | AlternatingLayoutEntry
    | CodeBlockEntry
    | BlogPostEntry
    | CaseStudyEntry
    | HighlightedLinkEntry
    | TextWithImagesEntry
    | GraphiQlEntry
    | ComponentVideoEntry

  const entryMap = new Map<string, ExpectedEntryTypes>()
  for (const entry of links.entries.block || []) {
    if (!entry) return
    entryMap.set(entry.sys.id, entry as ExpectedEntryTypes)
  }

  for (const entry of links.entries.inline || []) {
    if (!entry) return
    entryMap.set(entry.sys.id, entry as ExpectedEntryTypes)
  }

  return documentToReactComponents(json, {
    renderNode: {
      // Do not render empty paragraphs
      [BLOCKS.PARAGRAPH]: (node, children) =>
        children != '' ? <p>{children}</p> : '',

      // Embedded inline entries
      [INLINES.EMBEDDED_ENTRY]: (node, _children) => {
        // find the entry in the entryMap by ID
        const entry = entryMap.get(node.data.target.sys.id)

        if (!entry) return null

        // render the entries as needed
        switch (entry.__typename) {
          case 'BlogPost': {
            return (
              <NextLink href={`/blog/${entry.slug}`}>{entry.title}</NextLink>
            )
          }

          case 'CaseStudy': {
            return (
              <NextLink href={`${CASE_STUDY_SLUG}/${entry.slug}`}>
                {entry.title}
              </NextLink>
            )
          }

          default:
            return null
        }
      },

      // Embedded block entries
      [BLOCKS.EMBEDDED_ENTRY]: (node, _children) => {
        // find the entry in the entryMap by ID
        const entry = entryMap.get(node.data.target.sys.id)

        if (!entry) return null

        // render the entries as needed by looking at the __typename
        // referenced in the GraphQL query
        switch (entry.__typename) {
          case 'AlternatingLayout': {
            return <AlternatingLayout entry={entry} />
          }

          case 'CodeBlock': {
            return (
              <CodeBlock
                className={entry.language ? `language-${entry.language}` : ''}
                code={entry.code!}
              />
            )
          }

          case 'HighlightedLink': {
            return <HighlightedLink entry={entry} />
          }

          case 'TextWithImages': {
            return <TextWithImages entry={entry} />
          }

          case 'ComponentVideo':
            return <Video url={entry.url!} />

          case 'GraphiQl':
            return (
              <div className='spacer-sm'>
                <GraphiQL
                  storageKeyPrefix={null}
                  endpoint={entry.graphQlEndpoint!}
                  defaultQuery={entry.defaultQuery!}
                  className='h-[640px] bg-gray-100 rounded-6'
                />
              </div>
            )

          default:
            return null
        }
      },

      // Embedded asset entries
      [BLOCKS.EMBEDDED_ASSET]: (node, _next) => {
        // find the asset in the assetMap by ID
        const asset = assetMap.get(node.data.target.sys.id)

        if (asset && asset.width && asset.height) {
          const isGif = asset.contentType === 'image/gif'
          // render the asset accordingly
          return (
            <figure>
              <NextImage
                src={
                  isGif && (asset as any).webpUrl
                    ? (asset as any).webpUrl
                    : asset.url
                }
                width={asset.width}
                height={asset.height}
                alt={asset.title ?? ''}
              />
              <figcaption>{asset.title}</figcaption>
            </figure>
          )
        }

        return null
      },
    },
  })
}
